import { useEffect, useState } from "react";
import api, { API } from "../../lib/api";
import { Download, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminSubscribers() {
  const [list, setList] = useState([]);
  const [tours, setTours] = useState([]);
  const [announceOpen, setAnnounceOpen] = useState(false);
  const [tourId, setTourId] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/admin/subscribers").then(r => setList(r.data));
    api.get("/tours").then(r => setTours(r.data));
  }, []);

  const exportCsv = async () => {
    const token = localStorage.getItem("ms_admin_token");
    const r = await fetch(`${API}/admin/subscribers/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click();
  };

  const announce = async () => {
    if (!tourId || sending) return;
    setSending(true);
    try {
      const r = await api.post("/admin/subscribers/announce", { tour_id: tourId, origin_url: window.location.origin });
      toast.success(`Announcement sent to ${r.data.sent} of ${r.data.total} subscribers${r.data.failed ? ` (${r.data.failed} failed)` : ""}.`);
      setAnnounceOpen(false);
      setTourId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send announcement");
    } finally {
      setSending(false);
    }
  };

  const selectedTour = tours.find(t => t.id === tourId);
  const active = list.filter(s => !s.unsubscribed);

  return (
    <div className="p-10 md:p-12" data-testid="admin-subscribers">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="label-eyebrow text-ms-stone">Newsletter</p>
          <h1 className="serif text-4xl text-ms-earth mt-2">Subscribers</h1>
          <p className="text-sm text-ms-stone mt-2">{list.length} {list.length === 1 ? "signup" : "signups"} · {active.length} active. Synced to your Resend audience automatically.</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setAnnounceOpen(true)} disabled={active.length === 0} className="btn-primary-dark inline-flex items-center gap-2 !text-[11px] !py-2.5 !px-5 disabled:opacity-50" data-testid="announce-tour-btn">
            <PaperPlaneTilt size={16} /> Announce a tour
          </button>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2.5 border border-ms-edge rounded-md text-sm hover:bg-ms-cream" data-testid="subscribers-export-csv-btn">
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-ms-edge rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ms-cream border-b border-ms-edge">
            <tr>
              <th className="text-left p-4 label-eyebrow text-xs">Email</th>
              <th className="text-left p-4 label-eyebrow text-xs">Subscribed</th>
              <th className="text-left p-4 label-eyebrow text-xs">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map(sub => (
              <tr key={sub.email} className="border-b border-ms-edge last:border-0 hover:bg-ms-cream/40" data-testid={`subscriber-row-${sub.email}`}>
                <td className="p-4 text-ms-earth">{sub.email}</td>
                <td className="p-4 text-ms-stone">{sub.created_at ? new Date(sub.created_at).toLocaleString() : "—"}</td>
                <td className="p-4">
                  {sub.unsubscribed
                    ? <span className="text-ms-stone text-xs">Unsubscribed</span>
                    : sub.resend_synced
                      ? <span className="text-ms-success text-xs">Subscribed · synced</span>
                      : <span className="text-ms-brass text-xs">Subscribed · sync pending</span>}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-ms-stone">No subscribers yet. The footer "Stay in the loop" form feeds this list.</td></tr>}
          </tbody>
        </table>
      </div>

      {announceOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6" onClick={() => !sending && setAnnounceOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl p-8 w-full max-w-lg space-y-5" data-testid="announce-modal">
            <div>
              <p className="label-eyebrow text-ms-brass">Email blast</p>
              <h2 className="serif text-2xl text-ms-earth mt-1">Announce a tour</h2>
              <p className="text-sm text-ms-stone mt-2">Sends a branded "New mystery drops" email to all {active.length} active subscriber{active.length === 1 ? "" : "s"} with the tour photo, description, price, a booking link, and an unsubscribe link.</p>
            </div>
            <label className="block">
              <span className="label-eyebrow text-ms-stone text-xs">Tour</span>
              <select value={tourId} onChange={e => setTourId(e.target.value)} className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm bg-white" data-testid="announce-tour-select">
                <option value="">Choose a tour…</option>
                {tours.map(t => <option key={t.id} value={t.id}>{t.title}{t.is_active ? "" : " (inactive)"}</option>)}
              </select>
            </label>
            {selectedTour && (
              <div className="border border-ms-edge rounded-lg p-4 text-sm">
                <p className="text-ms-earth font-medium">{selectedTour.title}</p>
                {selectedTour.short_description && <p className="text-ms-stone mt-1">{selectedTour.short_description}</p>}
                {selectedTour.base_price > 0 && <p className="text-ms-brass mt-2 text-xs">${selectedTour.base_price.toFixed(2)} per guest</p>}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => setAnnounceOpen(false)} disabled={sending} className="px-4 py-2.5 border border-ms-edge rounded-md text-sm hover:bg-ms-cream" data-testid="announce-cancel-btn">Cancel</button>
              <button onClick={announce} disabled={!tourId || sending} className="btn-primary-dark !text-[11px] !py-2.5 !px-5 disabled:opacity-50" data-testid="announce-send-btn">
                {sending ? "Sending…" : `Send to ${active.length}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
